/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import moment from 'moment'
import { toast } from 'react-toastify'
import {
  DEVICE_COMPLAINT_CATEGORIES,
  DEVICE_COMPLAINT_STATUSES,
  DeviceComplaintSite,
} from 'constants/deviceComplaints'
import { useGetDeviceComplaintsQuery, useLazyGetDeviceComplaintFileUrlQuery } from 'store/services/deviceComplaints'
import { DeviceComplaintDto } from 'store/types'
import { DeviceComplaintModal } from 'components/organisms/deviceComplaints/DeviceComplaintModal'
import { DeviceComplaintStatusModal } from 'components/organisms/deviceComplaints/DeviceComplaintStatusModal'

interface DeviceComplaintsListProps {
  site: DeviceComplaintSite
  deviceId?: number
  orderId?: number
  patientId?: number
  deviceLabel?: string
  canFile?: boolean
  canManage?: boolean
}

const getLabel = (options: { value: string; label: string }[], value: string) =>
  options.find((option) => option.value === value)?.label ?? value

// Complaints of a device, an order or a patient, with filing and (lab) status handling
export const DeviceComplaintsList = ({
  site,
  deviceId,
  orderId,
  patientId,
  deviceLabel,
  canFile = true,
  canManage = false,
}: DeviceComplaintsListProps) => {
  const [isFiling, setIsFiling] = useState(false)
  const [editedComplaint, setEditedComplaint] = useState<DeviceComplaintDto | null>(null)
  const { data: complaints, isLoading } = useGetDeviceComplaintsQuery(
    { site, deviceId, orderId, patientId },
    { skip: !deviceId && !orderId && !patientId },
  )
  const [getFileUrl] = useLazyGetDeviceComplaintFileUrlQuery()

  const openForm = async (id: string) => {
    try {
      const { url } = await getFileUrl({ site, id }).unwrap()
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch {
      toast.error('Could not open the complaint form.')
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Device Complaints</h5>
        {canFile && deviceId && (
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setIsFiling(true)}>
            <i className="fas fa-exclamation-triangle mr-1" />
            Report Complaint
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-muted">Loading...</div>
      ) : !complaints?.length ? (
        <div className="text-muted">No complaints filed.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm" css={{ fontSize: '14px' }}>
            <thead>
              <tr>
                <th>Filed</th>
                <th>Status</th>
                <th>Category</th>
                <th>Details</th>
                <th>Form</th>
                {canManage && <th />}
              </tr>
            </thead>
            <tbody>
              {complaints.map((complaint) => {
                const status = DEVICE_COMPLAINT_STATUSES.find(({ value }) => value === complaint.status)

                return (
                  <tr key={complaint.id}>
                    <td className="text-nowrap">
                      {moment(complaint.createdAt).format('MM/DD/YYYY')}
                      <div className="text-muted">
                        {complaint.source} {complaint.reportedByName && `· ${complaint.reportedByName}`}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${status?.badgeClass ?? 'badge-secondary'}`}>
                        {status?.label ?? complaint.status}
                      </span>
                      {complaint.statusNotes && <div className="text-muted mt-1">{complaint.statusNotes}</div>}
                    </td>
                    <td>
                      {getLabel(DEVICE_COMPLAINT_CATEGORIES, complaint.category)}
                    </td>
                    <td css={{ whiteSpace: 'pre-wrap' }}>
                      {complaint.description}
                      <div className="text-muted">
                        {[
                          complaint.orderId && `Order #${complaint.orderId}`,
                          complaint.officeName,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </td>
                    <td>
                      <button type="button" className="btn btn-link btn-sm p-0" onClick={() => openForm(complaint.id)}>
                        <i className="fas fa-file-pdf mr-1" />
                        View
                      </button>
                    </td>
                    {canManage && (
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary text-nowrap"
                          onClick={() => setEditedComplaint(complaint)}>
                          Update Status
                        </button>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {deviceId && (
        <DeviceComplaintModal
          open={isFiling}
          onClose={() => setIsFiling(false)}
          site={site}
          deviceId={deviceId}
          orderId={orderId}
          deviceLabel={deviceLabel}
        />
      )}
      {canManage && <DeviceComplaintStatusModal complaint={editedComplaint} onClose={() => setEditedComplaint(null)} />}
    </div>
  )
}
